import { useMemo, useState, type CSSProperties } from "react";
import { Loader, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { Platform, Platforms } from "./types";
import { mapPlatformStatus, statusPill, unitDisplayStatus } from "./status";
import { FilterChips, type FilterChip } from "../FilterChips";

// Inline styles (not Tailwind): these views ship inside the robotics bundle,
// which does NOT include Tailwind — only Mantine (host-provided) + inline CSS.
const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate800: "#1e293b",
  slate900: "#0f172a",
  border: "#e2e8f0",
  white: "#ffffff",
  greenText: "#059669",
  blueText: "#2563eb",
  slate50: "#f8fafc",
};

interface PlatformsViewProps {
  data?: Platforms;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  locationLabel?: string;
  onSelectPlatform?: (platform: Platform) => void;
  platformSide?: boolean;
}

const STATUS_FILTERS = ["All", "Available", "In Transit", "At Dock", "Returning", "Maintenance", "Fault"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

function matchesSearch(platform: Platform, term: string) {
  if (!term) return true;
  const haystack = [
    platform.platformNumber,
    platform.currentUnitNumber ?? "",
    ...platform.units.flatMap((u) => [u.unitNumber, u.owner?.name ?? "", u.owner?.email ?? ""]),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(term);
}

const miniLabel: CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400 };

function StatusPill({ label }: { label: string }) {
  const p = statusPill(label);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: p.bg,
        color: p.text,
        border: `1px solid ${p.border}`,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 10px",
        borderRadius: 999,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
}

export function PlatformsView({
  data,
  isLoading,
  isError,
  error,
  locationLabel,
  onSelectPlatform,
  platformSide = false,
}: PlatformsViewProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const term = search.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      (data?.platforms ?? []).filter(
        (p) =>
          matchesSearch(p, term) &&
          (statusFilter === "All" || mapPlatformStatus(p.status) === statusFilter)
      ),
    [data?.platforms, term, statusFilter]
  );

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#991b1b", marginBottom: 8 }}>Error Loading Platforms</div>
        <div style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Something went wrong."}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400, marginBottom: 4 }}>
          Platforms — {locationLabel ?? "—"}
        </div>
        <div style={{ color: c.slate500 }}>
          Every platform and the units mapped to it. Click a unit to request it to the dock,
          return it to storage, or reset a stuck platform.
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
          <TextInput
            placeholder="Search platform or unit code…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
          />
        </div>
        <FilterChips
          chips={buildStatusChips(data.platforms)}
          active={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
        />
      </div>

      {data.platforms.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          No platforms configured for this location.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: c.slate500 }}>
          {search.trim()
            ? `No platforms match “${search.trim()}”${statusFilter !== "All" ? ` in “${statusFilter}”` : ""}.`
            : `No platforms are currently “${statusFilter}”.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filtered.map((platform) => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              onSelectPlatform={onSelectPlatform}
              platformSide={platformSide}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ALWAYS_SHOWN: StatusFilter[] = ["All", "Available", "In Transit", "At Dock", "Returning"];

function buildStatusChips(platforms: Platform[]): FilterChip[] {
  const counts: Record<string, number> = {};
  for (const p of platforms) {
    const label = mapPlatformStatus(p.status);
    counts[label] = (counts[label] ?? 0) + 1;
  }
  return STATUS_FILTERS.filter(
    (f) => ALWAYS_SHOWN.includes(f) || (counts[f] ?? 0) > 0
  ).map((f) => ({
    value: f,
    label: f,
    count: f === "All" ? platforms.length : counts[f] ?? 0,
    colors: f === "All" ? undefined : statusPill(f),
  }));
}

const unitGrid = (platformSide: boolean) =>
  `${platformSide ? "60px " : ""}minmax(110px, 1.2fr) 110px minmax(160px, 1.8fr)`;

function PlatformCard({
  platform,
  onSelectPlatform,
  platformSide = false,
}: {
  platform: Platform;
  onSelectPlatform?: (platform: Platform) => void;
  platformSide?: boolean;
}) {
  const UNIT_GRID = unitGrid(platformSide);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        borderRadius: 12,
        border: `1px solid ${c.border}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        background: c.white,
        overflow: "hidden",
      }}
    >
      {/* Left: platform label, spanning the unit rows */}
      <div
        onClick={() => onSelectPlatform?.(platform)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
        }}
        style={{
          width: 120,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: 16,
          borderRight: `1px solid ${c.border}`,
          background: c.slate50,
          cursor: onSelectPlatform ? "pointer" : "default",
        }}
      >
        <div style={miniLabel}>Platform</div>
        <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, color: c.slate900 }}>
          {platform.platformNumber}
        </div>
      </div>

      {/* Middle: the units table */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: UNIT_GRID,
            gap: 12,
            padding: "8px 16px",
            borderBottom: `1px solid ${c.border}`,
          }}
        >
          {platformSide && <div style={miniLabel}>Side</div>}
          <div style={miniLabel}>Mapped Units ({platform.units.length})</div>
          <div style={miniLabel}>Current weight</div>
          <div style={miniLabel}>Owner</div>
        </div>

        {platform.units.length === 0 ? (
          <div
            onClick={() => onSelectPlatform?.(platform)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
            }}
            style={{ color: c.slate400, fontSize: 13, padding: "12px 16px", cursor: onSelectPlatform ? "pointer" : "default" }}
          >
            No units assigned yet.
          </div>
        ) : (
          platform.units.map((unit, i) => {
            const isCurrent = platform.currentUnitId === unit.unitId;
            return (
              <div
                key={unit.unitId}
                onClick={() => onSelectPlatform?.(platform)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onSelectPlatform?.(platform);
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: UNIT_GRID,
                  gap: 12,
                  alignItems: "center",
                  padding: "10px 16px",
                  borderBottom: i < platform.units.length - 1 ? `1px solid ${c.border}` : undefined,
                  background: isCurrent ? statusPill(unitDisplayStatus(platform, unit)).bg : c.white,
                  cursor: onSelectPlatform ? "pointer" : "default",
                }}
              >
                {platformSide && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.slate900 }}>
                    {unit.platformSide ?? "—"}
                  </div>
                )}
                <div style={{ fontWeight: 700, color: c.slate900 }}>Unit {unit.unitNumber}</div>
                <div style={{ fontSize: 13, color: c.slate500 }}>
                  {unit.currentWeight != null ? `${unit.currentWeight} kg` : "—"}
                </div>
                <div style={{ minWidth: 0 }}>
                  {unit.owner ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.slate900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {unit.owner.name || "—"}
                      </div>
                      {unit.owner.email && (
                        <div style={{ fontSize: 12, color: c.slate400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {unit.owner.email}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 13, color: c.slate400, fontStyle: "italic" }}>Not rented</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Right: platform status, spanning — fixed width so cards align regardless of label */}
      <div style={{ width: 150, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 16px", borderLeft: `1px solid ${c.border}` }}>
        <StatusPill label={mapPlatformStatus(platform.status)} />
      </div>
    </div>
  );
}
